import { useContext, useEffect, useState, useCallback } from "react";
import { UseFormReturn, FieldValues, Path } from "react-hook-form";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { MutationResponse } from "@/models";

type UseRecordSubmissionOptions<T extends FieldValues> = {
  form: UseFormReturn<T, any, any>;
  recordType: string;
  toastMessage?: string | (() => string);
  successMessage?: string | (() => string);
  onSuccess?: () => void;
};

type UseRecordSubmissionReturn<T extends FieldValues> = {
  isSubmitting: boolean;
  setSubmitResult: React.Dispatch<
    React.SetStateAction<MutationResponse<T> | undefined>
  >;
  handleSubmitStart: () => void;
};

export function useRecordSubmission<T extends FieldValues>({
  form,
  recordType,
  toastMessage,
  successMessage,
  onSuccess,
}: UseRecordSubmissionOptions<T>): UseRecordSubmissionReturn<T> {
  const { addPendingFileSubmission, showToast } = useContext(
    FileSubmissionsContext
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<
    MutationResponse<T> | undefined
  >();

  const handleSubmitStart = useCallback(() => {
    setIsSubmitting(true);
  }, []);

  useEffect(() => {
    if (submitResult === undefined) return;

    setIsSubmitting(false);

    if (submitResult.serverError) {
      showToast(
        "error",
        "Error",
        submitResult.serverError,
        `error-${recordType.toLowerCase()}`
      );
    } else if (submitResult.fileSubmission !== null) {
      const resolvedSuccessMessage =
        typeof successMessage === "function"
          ? successMessage()
          : successMessage;
      addPendingFileSubmission(
        submitResult.fileSubmission,
        resolvedSuccessMessage
      );
      if (toastMessage) {
        const resolvedToastMessage =
          typeof toastMessage === "function" ? toastMessage() : toastMessage;
        showToast(
          "processing",
          "Processing",
          resolvedToastMessage,
          `processing-${recordType.toLowerCase()}`
        );
      }
      onSuccess?.();
    } else {
      (submitResult.errors ?? []).forEach((e) =>
        form.setError(e.Fieldname as Path<T>, {
          type: "manual",
          message: e.Message,
        })
      );
    }

    setSubmitResult(undefined);
  }, [
    submitResult,
    addPendingFileSubmission,
    showToast,
    toastMessage,
    successMessage,
    recordType,
    onSuccess,
    form,
  ]);

  return {
    isSubmitting,
    setSubmitResult,
    handleSubmitStart,
  };
}
